var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import * as React from 'react';
import { styled } from '../theme-provider';
import { createA11yOutlineStyles } from '../helpers/a11y';
import { StyledIcon } from './card-header/card-header-chevron';
import { StyledCheckmarkWrapper } from './card-header/card-header-checkbox';
import { CardHeaderWrapper } from './card-header';
const CardContext = React.createContext({
    type: 'default',
    emphasis: 'large',
    isActive: false,
});
export const useCardContext = () => React.useContext(CardContext);
export const Card = React.forwardRef((props, ref) => {
    const { children, emphasis, type, 'data-testid': dataTestId, role, ariaLabel, } = props;
    const [ariaControlsIds, setAriaControlsIds] = React.useState('');
    const handleToggle = React.useCallback((event) => {
        const target = event.target;
        const isClickOnCardWrapper = target.tagName === 'DIV';
        if (isExtendedCardProps(props) &&
            props.onToggle &&
            isClickOnCardWrapper) {
            props.onToggle();
        }
    }, [props]);
    const isActive = isExtendedCardProps(props) ? props.isActive : false;
    const onToggle = isExtendedCardProps(props) ? handleToggle : undefined;
    const value = React.useMemo(() => ({
        setAriaControlsIds,
        type,
        emphasis,
        dataTestId,
        isActive,
        onToggle,
        role,
        ariaLabel,
    }), [dataTestId, emphasis, isActive, onToggle, type, role, ariaLabel]);
    return (React.createElement(CardContext.Provider, { value: value },
        React.createElement(CardInner, Object.assign({}, props, { ref: ref, ariaControlsIds: ariaControlsIds }), children)));
});
Card.displayName = 'Card';
const CardWrapper = styled.div.withConfig({ displayName: "CardWrapper", componentId: "sc-njn7df" }) `overflow:hidden;border-radius:${({ theme }) => theme.size.static150};box-shadow:inset 0px 0px 0px ${({ theme, isChecked }) => isChecked
    ? `2px ${theme.colors.border.active}`
    : `1px ${theme.colors.border.default}`};&:hover:not(:disabled){box-shadow:0px 0px 0px ${({ theme, isChecked }) => isChecked
    ? `2px ${theme.colors.border.active} inset`
    : `2px ${theme.colors.border.default}`};}img,.img{padding:0;border:0;}&:has(:focus-visible),&:focus-visible{${({ theme, type }) => type !== 'default' && createA11yOutlineStyles(theme, 'solid')}}&:focus:not(:focus-visible){outline:none;outline-offset:0;}padding:${({ theme }) => theme.size.static200};position:relative;display:grid;gap:${({ theme }) => theme.size.static200};color:${({ theme }) => theme.colors.content.primary};:hover,:focus-within{${CardHeaderWrapper}[aria-expanded],${CardHeaderWrapper}[aria-selected]{color:${({ theme }) => theme.colors.button.tertiary.hover};}${StyledIcon}{color:${({ theme }) => theme.colors.button.tertiary.hover};}}:hover{${StyledCheckmarkWrapper}{outline:${({ theme }) => theme.size.border.light};solid ${({ theme }) => theme.colors.border.default};}}`;
const isExtendedCardProps = (props) => props.type === 'collapsible' ||
    props.type === 'selectable';
const CardInner = React.forwardRef((props, ref) => {
    const { children, 'data-testid': dataTestId, uniqueAriaId, ariaControlsIds, type } = props, other = __rest(props, ["children", 'data-testid', "uniqueAriaId", "ariaControlsIds", "type"]);
    const { isActive, onToggle } = useCardContext();
    if (isExtendedCardProps(props)) {
        const isChecked = type === 'selectable' ? isActive : undefined;
        return (React.createElement(CardWrapper, Object.assign({ type: type, onClick: onToggle }, other, { id: uniqueAriaId, "aria-controls": 
            /* `aria-controls` should only control elements that are present in the DOM,
            after the Card is active the collapsed sections are visible, hence their `id` can be mapped
            to the `aria-controls` here*/
            type === 'collapsible' && isActive ? ariaControlsIds : undefined, isChecked: isChecked, "data-testid": dataTestId, ref: ref }), children));
    }
    return (React.createElement(CardWrapper, Object.assign({}, other, { "data-testid": dataTestId, ref: ref }), children));
});
CardInner.displayName = 'Card';
